import React, { useState, useEffect } from "react";
import {
	Box,
	Button,
	Card,
	Chip,
	Container,
	FormControl,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import HistoryTwoToneIcon from "@mui/icons-material/HistoryTwoTone";
import QrCodeScannerTwoToneIcon from "@mui/icons-material/QrCodeScannerTwoTone";
import ConfirmationNumberTwoToneIcon from "@mui/icons-material/ConfirmationNumberTwoTone";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink } from "react-router-dom";
import { CommonPagination } from "src/common/CommonPagination";
import { useGetQueryWithTotal } from "src/hooks/useGetQueryWithTotal";
import { useDebounce } from "src/hooks/useDebounce";
import type { IScanHistoryItem } from "../type/ticketScanType";
import ScanHistoryTable from "./ScanHistoryTable";

const ScanHistoryPage: React.FC = () => {
	const [historyPagination, setHistoryPagination] = useState({
		limit: 50,
		skip: 0,
	});
	const [scanTypeFilter, setScanTypeFilter] = useState<string>("");
	const [historySearch, setHistorySearch] = useState<string>("");
	const debouncedHistorySearch = useDebounce(historySearch, 500);

	useEffect(() => {
		setHistoryPagination((prev) => ({ ...prev, skip: 0 }));
	}, [debouncedHistorySearch, scanTypeFilter]);

	const { data: historyData, isLoading: isHistoryLoading } =
		useGetQueryWithTotal<IScanHistoryItem[]>({
			endPoint: "/api/v1/admin/visitor/ticket/scan",
			queryKeys: [
				"TicketScans",
				historyPagination.skip,
				historyPagination.limit,
				scanTypeFilter,
				debouncedHistorySearch,
			],
			params: {
				limit: historyPagination.limit,
				skip: historyPagination.skip,
				...(scanTypeFilter ? { scan_type: scanTypeFilter } : {}),
				...(debouncedHistorySearch.trim()
					? {

						searchQuery: debouncedHistorySearch.trim(),
					}
					: {}),
			},
		});

	const hasActiveFilters = Boolean(historySearch || scanTypeFilter);

	const handleClearFilters = () => {
		setHistorySearch("");
		setScanTypeFilter("");
	};

	return (
		<>
			<Helmet>
				<title>BTTF - Scan History</title>
			</Helmet>

			<Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
				{/* Page Header */}
				<Box
					sx={{
						mb: 3,
						display: "flex",
						flexDirection: { xs: "column", sm: "row" },
						alignItems: { xs: "flex-start", sm: "center" },
						justifyContent: "space-between",
						gap: 2,
					}}
				>
					<Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
							<HistoryTwoToneIcon />
							<Typography variant="h3" fontWeight={700}>
								Scan History
							</Typography>
						</Box>
					</Box>

					{/* Header Action Buttons */}
					<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
						<Button
							component={RouterLink}
							to="/visitor/scan-ticket"
							variant="contained"
							color="primary"
							startIcon={<QrCodeScannerTwoToneIcon />}
							sx={{
								borderRadius: 1.5,
								textTransform: "none",
								fontWeight: 700,
								boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
							}}
						>
							Scan Ticket
						</Button>

						<Button
							component={RouterLink}
							to="/visitor/ticket-list"
							variant="outlined"
							color="primary"
							startIcon={<ConfirmationNumberTwoToneIcon />}
							sx={{
								borderRadius: 1.5,
								textTransform: "none",
								fontWeight: 600,
							}}
						>
							Ticket List
						</Button>
					</Box>
				</Box>

				{/* Main Card */}
				<Card elevation={2} sx={{ overflow: "hidden", borderRadius: 2 }}>
					{/* Filter Bar */}
					<Box
						sx={{
							p: 2,
							borderBottom: "1px solid",
							borderColor: "divider",
						}}
					>
						<Box
							sx={{
								display: "flex",
								justifyContent: { xs: "stretch", sm: "flex-end" },
								alignItems: "center",
								flexWrap: "wrap",
								gap: 1.5,
							}}
						>
							{/* Search Field */}
							<TextField
								size="small"
								placeholder="Search ticket no"
								value={historySearch}
								onChange={(e) => setHistorySearch(e.target.value)}
								sx={{ width: { xs: "100%", sm: 220, md: 240 } }}
							/>

							{/* Scan Type Filter */}
							<FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 150 } }}>
								<Select
									value={scanTypeFilter}
									onChange={(e) => setScanTypeFilter(e.target.value)}
									displayEmpty
									renderValue={(val) => {
										if (!val) return "All Scan Types";
										return val === "IN" ? "IN (Entry)" : "OUT (Exit)";
									}}
								>
									<MenuItem value="">All Scan Types</MenuItem>
									<MenuItem value="IN">IN (Entry)</MenuItem>
									<MenuItem value="OUT">OUT (Exit)</MenuItem>
								</Select>
							</FormControl>

							{/* Clear Filters */}
							{hasActiveFilters && (
								<Button
									size="small"
									color="error"
									onClick={handleClearFilters}
									sx={{ textTransform: "none", fontWeight: 600 }}
								>
									Clear
								</Button>
							)}
						</Box>
					</Box>

					{/* Scan History Table */}
					<ScanHistoryTable
						data={historyData?.data || []}
						isLoading={isHistoryLoading}
						paginationSkip={historyPagination.skip}
					/>

					{/* Pagination */}
					{historyData && historyData.total > 0 && (
						<Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
							<CommonPagination
								total={historyData.total}
								dataShowPerPage={historyPagination.limit}
								paginationCalFun={(limit, skip) =>
									setHistoryPagination({ limit, skip })
								}
							/>
						</Box>
					)}
				</Card>
			</Container>
		</>
	);
};

export default ScanHistoryPage;
